import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, MapPin, Loader2, CheckCircle2, Navigation } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import api from '../lib/api';
import { toast } from 'sonner';

// Santa Cruz de Tenerife center
const SC_CENTER = [28.4636, -16.2518];

export default function CreateIncidentModalV2({ onClose, onSuccess }) {
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [formData, setFormData] = useState({
    tipos: '',
    geolatitud: '',
    geolongitud: '',
    contenido: '',
    emplazamiento: '',
    comentario: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const initializingRef = useRef(false);

  // Fetch incident types
  useEffect(() => {
    api.get('/incident-types')
      .then(r => setIncidentTypes(r.data.types || []))
      .catch(() => toast.error('Error al cargar tipos de incidencias'))
      .finally(() => setLoadingTypes(false));
  }, []);

  // Init Leaflet map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || initializingRef.current) return;
    initializingRef.current = true;

    let L;
    let map;

    const initMap = async () => {
      try {
        L = (await import('leaflet')).default;
        await import('leaflet/dist/leaflet.css');

        // Fix default marker icons
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        });

        map = L.map(mapContainerRef.current, { zoomControl: true }).setView(SC_CENTER, 13);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap',
          maxZoom: 19,
        }).addTo(map);

        map.on('click', (e) => {
          const { lat, lng } = e.latlng;
          const latStr = lat.toFixed(6);
          const lngStr = lng.toFixed(6);

          if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
          } else {
            markerRef.current = L.marker([lat, lng]).addTo(map);
          }

          setFormData(prev => ({ ...prev, geolatitud: latStr, geolongitud: lngStr }));
        });

        mapRef.current = map;
      } catch (e) {
        console.error('Map init error:', e);
      }
    };

    initMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, []);

  // When lat/lon change externally (e.g. "use my location"), move marker
  const updateMarker = useCallback((lat, lng) => {
    if (!mapRef.current) return;
    import('leaflet').then(mod => {
      const L = mod.default;
      const latLng = [parseFloat(lat), parseFloat(lng)];
      if (markerRef.current) {
        markerRef.current.setLatLng(latLng);
      } else {
        markerRef.current = L.marker(latLng).addTo(mapRef.current);
      }
      mapRef.current.setView(latLng, 16);
    });
  }, []);

  const handleGetLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocalización no soportada');
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latStr = pos.coords.latitude.toFixed(6);
        const lngStr = pos.coords.longitude.toFixed(6);
        setFormData(prev => ({ ...prev, geolatitud: latStr, geolongitud: lngStr }));
        updateMarker(latStr, lngStr);
        setGettingLocation(false);
        toast.success('Ubicación obtenida');
      },
      (err) => {
        setGettingLocation(false);
        toast.error('Error: ' + err.message);
      }
    );
  };

  const handleLatLonChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      const lat = parseFloat(field === 'geolatitud' ? value : prev.geolatitud);
      const lng = parseFloat(field === 'geolongitud' ? value : prev.geolongitud);
      if (!isNaN(lat) && !isNaN(lng)) updateMarker(lat, lng);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tipos) return toast.error('Selecciona un tipo de incidencia');
    if (!formData.geolatitud || !formData.geolongitud) return toast.error('Selecciona la ubicación en el mapa');

    setIsSubmitting(true);
    try {
      const payload = {
        tipos: formData.tipos,
        geolatitud: String(formData.geolatitud),
        geolongitud: String(formData.geolongitud),
        contenido: formData.contenido,
        emplazamiento: formData.emplazamiento,
        comentario: formData.comentario,
        camposExtra: [],
      };
      const response = await api.post('/incidents/create', payload);
      if (response.data.success) {
        toast.success('Incidencia creada correctamente');
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error('Error al crear incidencia');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map(d => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(' — ')
        : (detail || 'Error al crear incidencia');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between pb-3 sticky top-0 bg-card z-10 border-b">
          <CardTitle className="text-xl">Crear Nueva Incidencia</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isSubmitting}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Tipo de Incidencia */}
            <div className="space-y-2">
              <Label htmlFor="tipos">
                Tipo de Incidencia <span className="text-red-500">*</span>
              </Label>
              {loadingTypes ? (
                <div className="flex items-center text-sm text-muted-foreground gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando tipos...
                </div>
              ) : (
                <select
                  id="tipos"
                  data-testid="incident-type-select"
                  value={formData.tipos}
                  onChange={(e) => setFormData(prev => ({ ...prev, tipos: e.target.value }))}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">— Selecciona un tipo —</option>
                  {incidentTypes.map((type) => (
                    <option key={type.id} value={String(type.id)}>
                      {type.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Mini mapa Leaflet */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Ubicación <span className="text-red-500">*</span>
                <span className="text-xs text-muted-foreground font-normal">(haz clic en el mapa para seleccionar)</span>
              </Label>
              <div
                ref={mapContainerRef}
                data-testid="location-map"
                className="w-full rounded-md border border-input overflow-hidden"
                style={{ height: '240px' }}
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="lat" className="text-xs text-muted-foreground">Latitud</Label>
                  <Input
                    id="lat"
                    data-testid="lat-input"
                    type="number"
                    step="any"
                    placeholder="28.4636"
                    value={formData.geolatitud}
                    onChange={(e) => handleLatLonChange('geolatitud', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="lng" className="text-xs text-muted-foreground">Longitud</Label>
                  <Input
                    id="lng"
                    data-testid="lng-input"
                    type="number"
                    step="any"
                    placeholder="-16.2518"
                    value={formData.geolongitud}
                    onChange={(e) => handleLatLonChange('geolongitud', e.target.value)}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGetLocation}
                disabled={gettingLocation}
                className="w-full"
                data-testid="use-my-location-btn"
              >
                {gettingLocation ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Obteniendo ubicación...</>
                ) : (
                  <><Navigation className="w-4 h-4 mr-2" /> Usar mi ubicación actual</>
                )}
              </Button>
            </div>

            {/* Emplazamiento */}
            <div className="space-y-2">
              <Label htmlFor="emplazamiento">Emplazamiento</Label>
              <Input
                id="emplazamiento"
                data-testid="emplazamiento-input"
                placeholder="Ej: Fachada del edificio, Acera frente al número 10"
                value={formData.emplazamiento}
                onChange={(e) => setFormData(prev => ({ ...prev, emplazamiento: e.target.value }))}
              />
            </div>

            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="contenido">Descripción / Observaciones</Label>
              <Textarea
                id="contenido"
                data-testid="contenido-input"
                rows={3}
                placeholder="Describe la incidencia con el mayor detalle posible..."
                value={formData.contenido}
                onChange={(e) => setFormData(prev => ({ ...prev, contenido: e.target.value }))}
              />
            </div>

            {/* Comentario de respuesta */}
            <div className="space-y-2">
              <Label htmlFor="comentario">Comentario de respuesta (opcional)</Label>
              <Textarea
                id="comentario"
                data-testid="comentario-input"
                rows={2}
                placeholder="Comentario inicial sobre la gestión de esta incidencia..."
                value={formData.comentario}
                onChange={(e) => setFormData(prev => ({ ...prev, comentario: e.target.value }))}
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting} className="flex-1">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !formData.tipos || (!formData.geolatitud || !formData.geolongitud)}
                className="flex-1"
                data-testid="create-incident-submit"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando...</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-2" /> Crear Incidencia</>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
